import React, { useCallback, useState } from 'react';
import StyledForm from './StyledForm';
import { Autocomplete, Popper } from '@mui/material';
import { TextField } from '@mui/material';
import useRandomizedId from '@/lib/utils/useRandomizedId';
import DirectInviteEmailOption from './DirectInviteEmailOption';
import { MAX_INVITEES_PER_REQUEST } from '..';
import InviteButton from './InviteButton';
import AutocompleteValueChip from './AutocompleteValueChip';
import UserAutocompleteOption from './UserAutocompleteOption';
import useUserAutocompleteOptions from './useUserAutocompleteOptions';
import useBlockPortfolioItemCloseWhenDirty from '@/components/ChallengePortfolio/NormalView/portfolio-item/useBlockPortfolioItemCloseWhenDirty';
import { FormattedMessage } from 'react-intl';

type InviteUsersFormProps = {
  onInvite: (emails: string[]) => Promise<void>;
};

const InviteUsersForm = ({ onInvite }: InviteUsersFormProps) => {
  const autocompleteId = useRandomizedId('user-sharing-autocomplete');

  const [values, setValues] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');

  const [sharing, setSharing] = useState(false);

  const [userAutocompleteOptions, fullUsersMap] =
    useUserAutocompleteOptions(inputValue);

  const inputDisabled = values.length >= MAX_INVITEES_PER_REQUEST || sharing;

  const handleInvite = useCallback(async (): Promise<void> => {
    setSharing(true);

    await onInvite(values);

    setInputValue('');
    setValues([]);

    setSharing(false);
  }, [onInvite, setSharing, setInputValue, values, setValues]);

  useBlockPortfolioItemCloseWhenDirty(values.length > 0, handleInvite);

  return (
    <StyledForm
      onSubmit={(e: React.SubmitEvent<HTMLFormElement>): void => {
        e.preventDefault();

        handleInvite();
      }}
    >
      <Autocomplete
        id={autocompleteId}
        value={values}
        onChange={(_, newValues: string[]) => setValues(newValues)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        options={userAutocompleteOptions}
        getOptionKey={(user: string) => user}
        getOptionLabel={(user: string) => user}
        renderOption={({ key, ...optionProps }, email: string) => {
          const user = fullUsersMap.get(email);

          if (typeof user === 'undefined') {
            return (
              <DirectInviteEmailOption
                email={email}
                {...optionProps}
                key={key}
              />
            );
          }

          return (
            <UserAutocompleteOption user={user} {...optionProps} key={key} />
          );
        }}
        renderValue={(values: readonly string[], getItemProps) =>
          values.map((email: string, index: number) => {
            const { key, ...itemProps } = getItemProps({ index });

            const user = fullUsersMap.get(email);

            return (
              <AutocompleteValueChip
                user={user}
                userEmail={email}
                {...itemProps}
                key={key}
              />
            );
          })
        }
        renderInput={params => (
          <TextField
            variant="standard"
            {...params}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: undefined,
                disabled: inputDisabled,
                slotProps: {
                  input: {
                    disabled: inputDisabled,
                  },
                },
              },
            }}
            label={
              <FormattedMessage id="portfolio.challenge.sharing.invite_users.form.autocomplete_label" />
            }
            disabled={inputDisabled}
          />
        )}
        filterOptions={(options: string[]): string[] => {
          // This is necessary to disable the built-in Autocomplete filtering;
          // filtering is carried out manually by the backend user search.

          return options;
        }}
        slots={{
          popper: props => {
            if (userAutocompleteOptions.length === 0) {
              return null;
            }

            return <Popper {...props} />;
          },
        }}
        multiple
        fullWidth
      />
      <InviteButton disabled={values.length === 0} loading={sharing} />
    </StyledForm>
  );
};

export default InviteUsersForm;
